import React from "react";
import { CheckOutlined, GlobalOutlined, DownOutlined } from '@ant-design/icons';
import { Menu, Dropdown } from "antd";
import { useQuery } from "@tanstack/react-query";
import { connect } from "react-redux";
import AppLocale from "lang";
import languageFallbackData from "assets/data/language.data.json";
import { onLocaleChange } from "redux/actions/Theme";
import SettingsService from "services/SettingsService";

const supportedLocales = Object.keys(AppLocale);

const normalizeLocale = (locale) => {
	if (!locale) {
		return '';
	}
	const localeString = String(locale).trim().toLowerCase();
	const [baseLocale] = localeString.split(/[-_]/);
	return baseLocale || '';
};

const mapFallbackLanguages = () => {
	return languageFallbackData
		.map((elm) => {
			const locale = normalizeLocale(elm.langId);
			if (!supportedLocales.includes(locale)) {
				return null;
			}
			return {
				locale,
				langName: elm.langName,
				icon: elm.icon
			};
		})
		.filter(Boolean);
};

const mapApiLanguages = (languages = []) => {
	return languages
		.filter((elm) => elm?.is_active)
		.map((elm) => {
			const locale = normalizeLocale(elm?.code);
			if (!supportedLocales.includes(locale)) {
				return null;
			}
			return {
				locale,
				langName: elm?.name || elm?.code || locale,
				icon: elm?.icon
			};
		})
		.filter(Boolean);
};

const getLanguageOptions = (languagesFromApi) => {
	const apiLanguages = mapApiLanguages(languagesFromApi);
	const sourceLanguages = apiLanguages.length ? apiLanguages : mapFallbackLanguages();
	return sourceLanguages.filter(
		(language, index, languages) =>
			languages.findIndex((item) => item.locale === language.locale) === index
	);
};

const getLanguageDetail = (locale, languageOptions) => {
	const normalizedLocale = normalizeLocale(locale);
	return languageOptions.find((elm) => elm.locale === normalizedLocale) || languageOptions[0];
};

const LanguageIcon = ({ icon, langName }) => {
	const isFlagCode = typeof icon === 'string' && /^[a-z]{2}$/i.test(icon);
	if (isFlagCode) {
		return (
			<img
				style={{ maxWidth: '20px' }}
				src={`/img/flags/${icon.toLowerCase()}.png`}
				alt={langName}
			/>
		);
	}

	return (
		<span style={{ fontSize: '16px', lineHeight: 1 }}>
			{icon || <GlobalOutlined />}
		</span>
	);
};

const SelectedLanguage = ({ locale, languageOptions }) => {
	const language = getLanguageDetail(locale, languageOptions);

	if (!language) {
		return null;
	}

	const { langName, icon } = language;

	return (
		<div className="d-flex align-items-center">
			<LanguageIcon icon={icon} langName={langName} />
			<span className="font-weight-semibold ml-2">
				{langName} <DownOutlined className="font-size-xs" />
			</span>
		</div>
	);
};

export const NavLanguage = ({ locale, configDisplay, onLocaleChange }) => {
	const { data: languagesData } = useQuery(SettingsService.getLanguages());
	const languageOptions = getLanguageOptions(languagesData?.data);
	const activeLocale = normalizeLocale(locale);

	const languageOption = (
		<Menu>
			{languageOptions.map((elm) => (
				<Menu.Item
					key={elm.locale}
					className={activeLocale === elm.locale ? 'ant-dropdown-menu-item-active' : ''}
					onClick={() => onLocaleChange(elm.locale)}
				>
					<span className="d-flex justify-content-between align-items-center">
						<div>
							<LanguageIcon icon={elm.icon} langName={elm.langName} />
							<span className="font-weight-normal ml-2">{elm.langName}</span>
						</div>
						{activeLocale === elm.locale ? <CheckOutlined className="text-success" /> : null}
					</span>
				</Menu.Item>
			))}
		</Menu>
	);

	return (
		<Dropdown placement="bottomRight" overlay={languageOption} trigger={["click"]}>
			{configDisplay ? (
				<a href="#/" className="text-gray" onClick={(e) => e.preventDefault()}>
					<SelectedLanguage locale={activeLocale} languageOptions={languageOptions} />
				</a>
			) : (
				<Menu mode="horizontal">
					<Menu.Item key="language">
						<a href="#/" onClick={(e) => e.preventDefault()}>
							<GlobalOutlined className="nav-icon mr-0" />
						</a>
					</Menu.Item>
				</Menu>
			)}
		</Dropdown>
	);
};

const mapStateToProps = ({ theme }) => {
	const { locale } = theme;
	return { locale };
};

export default connect(mapStateToProps, { onLocaleChange })(NavLanguage);
